import React, { Suspense } from "react";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { LDUser } from "launchdarkly-js-client-sdk";

import AppErrorComponent from "AppErrorComponent";
import ErrorBoundary from "lib/ErrorBoundary";
import LoadingContainer from "LoadingContainer";
import Suspender from "lib/Suspender";
import { AccountTypeMap } from "lib/AccountType";
import { ActiveProfileQuery } from "operations";
import { apolloClient } from "lib/Apollo";
import { useActiveProfileQuery } from "graphql/queries/ActiveProfile";

const clientSideID = process.env.REACT_APP_LD_CLIENT_SIDE_ID || "";
const Anonymous = "anonymous";

const mapUser = ({ account, user }: ActiveProfileQuery): LDUser => ({
  anonymous: !account.id || !user.id,
  email: user.email ?? Anonymous,
  name: account.name ?? Anonymous,
  secondary: user.id,
  key: account.id,
  custom: {
    accountId: parseInt(account.id) ?? Anonymous,
    accountProfileName: account.name ?? Anonymous,
    accountType: account.type ? AccountTypeMap[account.type] : -1,
    userEmail: user.email ?? Anonymous,
    userId: parseInt(user.id) ?? Anonymous,
    username: user.displayName ?? Anonymous,
  },
});

export const Provider: React.FC = ({ children }) => {
  const queryResponse = useActiveProfileQuery({
    decodeNodeIds: true,
    client: apolloClient,
  });
  return (
    <ErrorBoundary FallbackComponent={AppErrorComponent}>
      <Suspense fallback={<LoadingContainer loading />}>
        <Suspender suspend={queryResponse.loading}>
          {queryResponse.data && (
            <LDProvider
              clientSideID={clientSideID}
              user={mapUser(queryResponse.data)}
              children={children}
            />
          )}
        </Suspender>
      </Suspense>
    </ErrorBoundary>
  );
};
